import { useMemo } from "react";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { useWorkspaceCloses } from "@api/close.api";
import { useEntitiesById } from "@hooks/useEntities";
import {
    toTaskMemberOption,
    useCloseLookups,
    useCloseStageLookups,
    useTaskMemberLookups,
} from "./taskWorkspaceDataLookups";

export function useTaskWorkspaceData() {
    const { data: workspaceMembers = [] } = useCurrentWorkspaceMembers();
    const { data: workspaceTaskGroups = [] } = useWorkspaceTaskGroups();
    const {
        closeStageIds,
        closeStagesMap,
        hasCloseStages,
        areCloseStagesLoading,
    } = useCloseStageLookups();
    const {
        data: closes = [],
        isFetched: areClosesFetched,
        isFetching: areClosesFetching,
    } = useWorkspaceCloses();
    const entitiesById = useEntitiesById();

    const memberOptions = useMemo(
        () => workspaceMembers.map(toTaskMemberOption),
        [workspaceMembers],
    );
    const { workspaceMemberIds, workspaceMembersMap } =
        useTaskMemberLookups(memberOptions);

    const workspaceTaskGroupIds = useMemo(
        () => workspaceTaskGroups.map((taskGroup) => taskGroup.id),
        [workspaceTaskGroups],
    );

    const workspaceTaskGroupsMap = useMemo(
        () =>
            new Map(
                workspaceTaskGroups.map((taskGroup) => [
                    taskGroup.id,
                    taskGroup.name,
                ]),
            ),
        [workspaceTaskGroups],
    );

    const { closeIds, closesMap, hasCloses } = useCloseLookups(closes);

    const entitiesNameMap = useMemo(() => {
        const map = new Map<number, string>();
        for (const [id, entity] of entitiesById) {
            map.set(id, entity.name ?? "");
        }
        return map;
    }, [entitiesById]);

    return {
        workspaceMemberIds,
        workspaceMembersMap,
        workspaceTaskGroupIds,
        workspaceTaskGroupsMap,
        closeStageIds,
        closeStagesMap,
        closeIds,
        closesMap,
        entitiesNameMap,
        hasCloseStages,
        hasCloses,
        areCloseStagesLoading,
        areClosesFetched,
        areClosesFetching,
    };
}

export type TaskWorkspaceData = ReturnType<typeof useTaskWorkspaceData>;
